function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Card, CardBody, CardHeader, Row, Col, Button } from 'reactstrap';
import { createHashHistory } from 'history';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import entityComposition from '../common/classes/entityComposition';
import checkBrowser from '../../utils/checkBrowser';
import StatusLoader from '../common/statusLoader';
import reportTypeSelector from './reportTypeSelector';
import { canEditSelector } from './reportSelectors';
import ReportContainer from './reportContainer';
import Icon from '../common/containers/icon';
import { getIsPermittedWithAnyOfThesePermissions, isCreator } from '../common/classes/permissions';
import PDF from '../common/pdf/pdf';
import PDF_VIEWER_CARD_HEIGHT from '../common/pdf/pdfViewerLayout';
const hashHistory = createHashHistory();
const handleBack = () => {
  hashHistory.replace('/file-cabinet');
};
const calculatePercentage = (timeElapsed, totalTime = 600) => {
  if (timeElapsed < 10) {
    return Math.floor(2.5 * timeElapsed);
  }
  return Math.floor(timeElapsed / totalTime * 0.75 * 100) + 24;
};
const Report = ({
  reportTypeFilterAddActions,
  hasAnyDynamicReportTypes,
  reportTypeFilterAdd,
  notificationActions,
  canEdit: _canEdit = false,
  reportActions,
  report,
  match
}) => {
  const {
    params: {
      id
    }
  } = match;
  const {
    active,
    error,
    loading
  } = report;
  const {
    indexQuery,
    list,
    loading: reportTypeLoading
  } = reportTypeFilterAdd;
  const {
    serverIndex
  } = reportTypeFilterAddActions;
  if (!id) {
    throw new Error('props.params.id is required');
  }
  const [loadedPercentage, setLoadedPercentage] = useState(0);
  const loadedTime = useRef(new Date()).current;
  const timerInterval = useRef(null);
  const handleEdit = useCallback(() => {
    hashHistory.replace(`/reports/edit/${id}`);
  }, [id]);

  // Separate the timer logic into its own useEffect to avoid dependency cycles
  useEffect(() => {
    const loadTime = () => {
      if (active && active.signedURL) {
        clearInterval(timerInterval.current);
        return;
      }
      const timeElapsed = (new Date() - loadedTime) / 1000;
      const newLoadedPercentage = calculatePercentage(timeElapsed);
      setLoadedPercentage(newLoadedPercentage);
    };

    // Start the timer
    timerInterval.current = setInterval(loadTime, 100);

    // Clean up the timer when component unmounts or when active.signedURL becomes available
    return () => {
      clearInterval(timerInterval.current);
    };
  }, [active, loadedTime]); // Only re-create the timer when active changes

  // Separate the data fetching into its own useEffect
  useEffect(() => {
    // Fetch the report data
    reportActions.serverRenderReport(id);

    // Fetch report types if needed
    if (!list.length && !reportTypeLoading) {
      serverIndex(indexQuery);
    }

    // Clean up when unmounting
    return () => {
      reportActions.showCancel(); // clear the active report when navigating away
    };
  }, [id, reportActions, serverIndex, indexQuery, list.length, reportTypeLoading]);
  const showEdit = hasAnyDynamicReportTypes && (active === null || active === void 0 ? void 0 : active.content) !== null && typeof (active === null || active === void 0 ? void 0 : active.content) !== 'undefined';
  let reportTitle = '';
  let url = 'invalidURL';
  let docTitle = '';
  if (active && active.reportType && active.reportType.name && active.reportNumber) {
    docTitle = `${active.reportType.name} ${active.reportNumber}`;
    reportTitle = /*#__PURE__*/React.createElement("h3", {
      className: "t-r-title"
    }, docTitle, "\xA0(", /*#__PURE__*/React.createElement("span", {
      style: active.statusName === 'Rejected' ? {
        color: 'red'
      } : {}
    }, active.statusName), ")");
  }
  if (id && active && active.signedURL) {
    url = active.signedURL;
  }
  const buttonStyle = {
    marginRight: '5px',
    float: 'left'
  };
  let title = 'Report not editable.';
  let disabled = true;
  const canEditInReadOnly = getIsPermittedWithAnyOfThesePermissions(['report_read_only']) && !isCreator(active === null || active === void 0 ? void 0 : active.appCreatorID);
  if (_canEdit) {
    disabled = false;
    title = 'Edit report';
  }
  if (canEditInReadOnly) {
    disabled = true;
  }
  const isMobileOS = checkBrowser.isMobile();
  const editButton = /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    className: "t-r-edit",
    title: title,
    disabled: disabled,
    onClick: handleEdit,
    style: buttonStyle
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "pencil-alt"
  }), " Edit");
  const header = /*#__PURE__*/React.createElement("div", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: showEdit && !isMobileOS ? 3 : 2
  }, /*#__PURE__*/React.createElement(Button, {
    className: "t-r-back",
    size: "sm",
    onClick: handleBack,
    style: buttonStyle
  }, "File Cabinet"), showEdit && !isMobileOS ? editButton : null), /*#__PURE__*/React.createElement(Col, {
    sm: showEdit ? 9 : 10
  }, reportTitle)));
  let content = /*#__PURE__*/React.createElement(StatusLoader, {
    percentage: loadedPercentage,
    loaded: !active
  });

  // Fill the card body so Light/Dark overflow rules cannot clip the PDF, without
  // nested card/body scrollbars (browser PDF chrome scrolls pages) — CU-86e2zb5tt.
  const showingPdf = Boolean(active && active.signedURL);
  if (showingPdf) {
    content = /*#__PURE__*/React.createElement(PDF, {
      className: "t-doc-report-detail-pdf",
      title: docTitle,
      src: url,
      button: editButton,
      hideTitleIfMobile: true,
      nativeRenderer: true,
      containerMaxHeight: "100%"
    });
  } else if (!loading) {
    content = /*#__PURE__*/React.createElement("div", {
      style: {
        paddingLeft: '20px'
      }
    }, /*#__PURE__*/React.createElement("h3", null, "There was a problem rendering this report"), /*#__PURE__*/React.createElement("p", null, "Please try again later"));
  }

  // Handle error notifications
  useEffect(() => {
    if (error) {
      // print the error msg if possible
      if (error.data && error.data.error_msg) {
        notificationActions.error({
          message: error.data.error_msg,
          id: 'report_render_error'
        });
      }
      // print the whole error
      notificationActions.error({
        message: error
      });
    }
  }, [error, notificationActions]);
  return /*#__PURE__*/React.createElement(ReportContainer, null, /*#__PURE__*/React.createElement(Card, {
    className: "t-pdf-viewer-card",
    style: {
      height: PDF_VIEWER_CARD_HEIGHT,
      display: 'flex',
      flexDirection: 'column'
    }
  }, /*#__PURE__*/React.createElement(CardHeader, {
    className: "card-heading-tight"
  }, header), /*#__PURE__*/React.createElement(CardBody, {
    style: _objectSpread({
      flex: 1,
      minHeight: 0,
      overflow: 'hidden'
    }, showingPdf ? {
      padding: 0
    } : {})
  }, content)));
};
Report.propTypes = {
  hasAnyDynamicReportTypes: PropTypes.bool.isRequired,
  report: PropTypes.shape({
    active: PropTypes.shape({
      appCreatorID: PropTypes.string,
      signedURL: PropTypes.string,
      content: PropTypes.shape({}),
      reportType: PropTypes.shape({
        name: PropTypes.string
      }),
      reportNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      statusName: PropTypes.string
    }),
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    loading: PropTypes.bool.isRequired,
    pendingChanges: PropTypes.bool
  }).isRequired,
  reportActions: PropTypes.shape({
    serverRenderReport: PropTypes.func,
    showCancel: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  canEdit: PropTypes.bool,
  reportTypeFilterAdd: PropTypes.shape({
    loading: PropTypes.bool,
    list: PropTypes.arrayOf(PropTypes.shape({})),
    indexQuery: PropTypes.shape({
      sortDirection: PropTypes.string
    }).isRequired
  }).isRequired,
  reportTypeFilterAddActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired
};
export { Report as TestReport };
export default entityComposition(Report, ['report', 'reportTypeFilterAdd'], {
  hasAnyDynamicReportTypes: state => reportTypeSelector.getDynamicReportTypes(state).length > 0,
  canEdit: canEditSelector
}, ['notification']);